import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiHome, FiBook, FiVideo, FiPlus, FiEdit, FiTrash2, FiEye, FiFileText, FiUsers, FiSearch, FiStar, FiImage } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CourseCard from '../../components/common/CourseCard';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { teacherDashboardAPI, teacherCourseAPI, teacherGradeAPI, teacherSubjectAPI } from '../../api/teacherEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  ContentGrid,
  EmptyState,
  FilterBar,
  FilterSelect,
  SearchInput,
} from './Dashboard.styles';

const TeacherDashboard = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    grade_id: '',
    subject_id: '',
    is_series: '',
  });
  const [imageLoaded, setImageLoaded] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    grade_id: '',
    term: 'Term 1',
    pass_mark: 70,
    is_series: false,
  });

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/teacher' },
    { icon: <FiUsers />, label: 'Grades', path: '/teacher/grades' },
    { icon: <FiBook />, label: 'Subjects', path: '/teacher/subjects' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Filter courses based on search and filters
  useEffect(() => {
    let filtered = courses;

    if (filters.search) {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        course.description.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.grade_id) {
      filtered = filtered.filter(course => course.grade_id === filters.grade_id);
    }

    if (filters.subject_id) {
      filtered = filtered.filter(course => course.subject_id === filters.subject_id);
    }

    if (filters.is_series !== '') {
      filtered = filtered.filter(course => course.is_series === (filters.is_series === 'true'));
    }

    setFilteredCourses(filtered);
  }, [courses, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardRes, coursesRes, gradesRes, subjectsRes] = await Promise.all([
        teacherDashboardAPI.getDashboard(),
        teacherCourseAPI.getCourses({ page: 1, per_page: 20 }),
        teacherGradeAPI.getGrades(),
        teacherSubjectAPI.getSubjects(),
      ]);
      
      setStats(dashboardRes.data.statistics);
      setCourses(coursesRes.data.course_contents || []);
      setGrades(gradesRes.data.grades || []);
      setSubjects(subjectsRes.data.subjects || []);
    } catch (error) {
      showToast('error', 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      await teacherCourseAPI.createCourse(formData);
      showToast('success', 'Course created successfully!');
      setShowCreateModal(false);
      setFormData({
        title: '',
        description: '',
        grade_id: '',
        term: 'Term 1',
        pass_mark: 70,
        is_series: false,
      });
      fetchData();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to create course');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCourse = (e, course) => {
    e.stopPropagation(); // Prevent card click
    setCourseToDelete(course);
    setShowDeleteModal(true);
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;
    
    try {
      await teacherCourseAPI.deleteCourse(courseToDelete.id);
      showToast('success', 'Course deleted successfully');
      setShowDeleteModal(false);
      setCourseToDelete(null);
      fetchData();
    } catch (error) {
      showToast('error', 'Failed to delete course');
    }
  };

  const handleCourseClick = (course) => {
    // Navigate to teacher course management page
    navigate(`/teacher/courses/${course.id}`);
  };

  const handleEditCourse = (e, course) => {
    e.stopPropagation(); // Prevent card click
    navigate(`/teacher/courses/${course.id}/edit`);
  };

  const handlePublishCourse = async (courseId, title) => {
    try {
      await teacherCourseAPI.publishCourse(courseId);
      showToast('success', `"${title}" published successfully!`);
      fetchData();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to publish course');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleViewCourse = (course) => {
    setSelectedCourse(course);
    setShowViewModal(true);
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <LoadingSpinner size="large" text="Loading dashboard..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Teacher Dashboard 👨‍🏫</h1>
            <p>Manage your courses and create content</p>
          </div>
          <Button
            leftIcon={<FiPlus />}
            onClick={() => window.location.href = '/teacher/create-course'}
          >
            Create Course
          </Button>
        </DashboardHeader>

        {stats && (
          <StatsGrid>
            <StatCard>
              <StatIcon color="#2c5f34">
                <FiBook />
              </StatIcon>
              <StatInfo>
                <h3>{stats.total_courses}</h3>
                <p>Total Courses</p>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon color="#10B981">
                <FiEye />
              </StatIcon>
              <StatInfo>
                <h3>{stats.published_courses}</h3>
                <p>Published</p>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon color="#F59E0B">
                <FiFileText />
              </StatIcon>
              <StatInfo>
                <h3>{stats.draft_courses}</h3>
                <p>Drafts</p>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon color="#EF4444">
                <FiVideo />
              </StatIcon>
              <StatInfo>
                <h3>{stats.total_students}</h3>
                <p>Total Students</p>
              </StatInfo>
            </StatCard>
          </StatsGrid>
        )}

            <SectionTitle>My Courses</SectionTitle>

            {/* Filter Bar */}
            <FilterBar>
              <SearchInput
                type="text"
                placeholder="Search courses..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                leftIcon={<FiSearch />}
              />
              <FilterSelect
                value={filters.grade_id}
                onChange={(e) => handleFilterChange('grade_id', e.target.value)}
              >
                <option value="">All Grades</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>{grade.name}</option>
                ))}
              </FilterSelect>
              <FilterSelect
                value={filters.subject_id}
                onChange={(e) => handleFilterChange('subject_id', e.target.value)}
              >
                <option value="">All Subjects</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </FilterSelect>
              <FilterSelect
                value={filters.is_series}
                onChange={(e) => handleFilterChange('is_series', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="true">Series</option>
                <option value="false">Single Course</option>
              </FilterSelect>
            </FilterBar>

            {filteredCourses.length === 0 ? (
          <EmptyState>
            <FiBook size={48} />
            <h3>No courses yet</h3>
            <p>Create your first course to get started</p>
            <Button leftIcon={<FiPlus />} onClick={() => window.location.href = '/teacher/create-course'}>
              Create Course
            </Button>
          </EmptyState>
            ) : (
              <ContentGrid>
                {filteredCourses.map((course) => (
                  <div key={course.id} style={{ position: 'relative' }}>
                    <CourseCard
                      course={{
                        ...course,
                        is_published: course.is_published,
                        customContent: (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            marginTop: '8px'
                          }}>
                            <span>By {course.teacher?.username || course.teacher?.name || 'You'}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                              <FiStar style={{ color: '#F59E0B' }} />
                              <strong>{((course.average_rating || course.rating?.average || 0)?.toFixed ? (course.average_rating || course.rating?.average || 0).toFixed(1) : Number(course.average_rating || course.rating?.average || 0).toFixed(1))}</strong>
                              <span>({course.rating_count || course.rating?.count || 0})</span>
                            </span>
                          </div>
                        )
                      }}
                      showProgress={false}
                      showRating={false}
                      showInstructor={false}
                      showGrade={false}
                      onClick={handleCourseClick}
                    />
                    
                    {/* Action Buttons Overlay */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      display: 'flex',
                      gap: '4px',
                      zIndex: 10
                    }}>
                      <button
                        onClick={(e) => handleEditCourse(e, course)}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid #E5E7EB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#2c5f34';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                          e.target.style.color = 'inherit';
                        }}
                        title="Edit Course"
                      >
                        <FiEdit size={14} />
                      </button>
                      
                      <button
                        onClick={(e) => handleDeleteCourse(e, course)}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          background: 'rgba(255, 255, 255, 0.9)',
                          border: '1px solid #E5E7EB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = '#EF4444';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                          e.target.style.color = 'inherit';
                        }}
                        title="Delete Course"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </ContentGrid>
            )}

        {/* Create Course Modal */}
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create New Course"
          size="medium"
        >
          <form onSubmit={handleCreateCourse}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Input
                label="Course Title"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Mathematics - Addition and Subtraction"
                required
              />
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the course content"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                  }}
                  required
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  Grade
                </label>
                <select
                  value={formData.grade_id}
                  onChange={(e) => setFormData({ ...formData, grade_id: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    fontSize: '16px',
                  }}
                  required
                >
                  <option value="">Select Grade</option>
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>{grade.name}</option>
                  ))}
                </select>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                    Term
                  </label>
                  <select
                    value={formData.term}
                    onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '16px',
                    }}
                  >
                    <option value="Term 1">Term 1</option>
                    <option value="Term 2">Term 2</option>
                    <option value="Term 3">Term 3</option>
                  </select>
                </div>
                
                <Input
                  label="Pass Mark (%)"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.pass_mark}
                  onChange={(e) => setFormData({ ...formData, pass_mark: parseInt(e.target.value) })}
                  required
                />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' }}>
                <Button variant="ghost" onClick={() => setShowCreateModal(false)} type="button">
                  Cancel
                </Button>
                <Button type="submit" loading={creating}>
                  Create Course
                </Button>
              </div>
            </div>
          </form>
        </Modal>

        {/* Delete Course Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setCourseToDelete(null);
          }}
          title="Delete Course"
          size="small"
        >
          <div style={{ padding: '20px 0' }}>
            <p style={{ margin: '0 0 20px 0', color: theme.colors.textSecondary }}>
              Are you sure you want to delete this course? This action cannot be undone and will remove all videos, files, quizzes, and student progress.
            </p>
            {courseToDelete && (
              <div style={{ 
                background: theme.colors.backgroundSecondary, 
                padding: '16px', 
                borderRadius: '8px', 
                marginBottom: '20px',
                border: `1px solid ${theme.colors.border}`
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: theme.colors.textPrimary }}>
                  {courseToDelete.title}
                </h4>
                <p style={{ margin: 0, color: theme.colors.textSecondary, fontSize: '14px' }}>
                  {courseToDelete.description || 'No description'}
                </p>
                <div style={{ marginTop: '12px', display: 'flex', gap: '16px', fontSize: '14px' }}>
                  <span style={{ color: theme.colors.textTertiary }}>
                    Grade: {grades.find(g => g.id === courseToDelete.grade_id)?.name || 'N/A'}
                  </span>
                  <span style={{ color: theme.colors.textTertiary }}>
                    Videos: {courseToDelete.video_count || 0}
                  </span>
                  <span style={{ color: theme.colors.textTertiary }}>
                    Quizzes: {courseToDelete.quiz_count || 0}
                  </span>
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button 
                variant="ghost" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setCourseToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                onClick={confirmDeleteCourse}
                style={{ background: '#EF4444' }}
              >
                Delete Course
              </Button>
            </div>
          </div>
            </Modal>

            {/* View Course Modal */}
            <Modal
              isOpen={showViewModal}
              onClose={() => setShowViewModal(false)}
              title="Course Details"
              size="large"
            >
              {selectedCourse && (
                <div style={{ padding: '20px 0' }}>
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{ margin: '0 0 10px 0', color: theme.colors.textPrimary }}>
                      {selectedCourse.title}
                    </h3>
                    <p style={{ margin: '0 0 15px 0', color: theme.colors.textSecondary }}>
                      {selectedCourse.description || 'No description provided'}
                    </p>
                    <div style={{ 
                      display: 'flex', 
                      gap: '20px', 
                      flexWrap: 'wrap',
                      marginBottom: '20px'
                    }}>
                      <div>
                        <strong>Grade:</strong> {grades.find(g => g.id === selectedCourse.grade_id)?.name || 'N/A'}
                      </div>
                      <div>
                        <strong>Term:</strong> {selectedCourse.term || 'N/A'}
                      </div>
                      <div>
                        <strong>Pass Mark:</strong> {selectedCourse.pass_mark || 0}%
                      </div>
                      <div>
                        <strong>Type:</strong> {selectedCourse.is_series ? 'Series' : 'Single Course'}
                      </div>
                      <div>
                        <strong>Status:</strong> 
                        <span style={{ 
                          color: selectedCourse.is_published ? '#10B981' : '#F59E0B',
                          fontWeight: '600',
                          marginLeft: '5px'
                        }}>
                          {selectedCourse.is_published ? 'Published' : 'Draft'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: '20px',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      background: theme.colors.backgroundSecondary,
                      padding: '16px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <FiVideo style={{ fontSize: '24px', color: '#EF4444', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.textPrimary }}>
                        {selectedCourse.video_count || 0}
                      </div>
                      <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                        Videos
                      </div>
                    </div>
                    <div style={{
                      background: theme.colors.backgroundSecondary,
                      padding: '16px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <FiFileText style={{ fontSize: '24px', color: '#3B82F6', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.textPrimary }}>
                        {selectedCourse.file_count || 0}
                      </div>
                      <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                        Files
                      </div>
                    </div>
                    <div style={{
                      background: theme.colors.backgroundSecondary,
                      padding: '16px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <FiBook style={{ fontSize: '24px', color: '#10B981', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 'bold', color: theme.colors.textPrimary }}>
                        {selectedCourse.quiz_count || 0}
                      </div>
                      <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                        Quizzes
                      </div>
                    </div>
                  </div>

                  <div style={{ 
                    display: 'flex', 
                    gap: '12px', 
                    justifyContent: 'flex-end',
                    marginTop: '20px'
                  }}>
                    <Button 
                      variant="ghost" 
                      onClick={() => setShowViewModal(false)}
                    >
                      Close
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowViewModal(false);
                        handleEditCourse(selectedCourse);
                      }}
                    >
                      Edit Course
                    </Button>
                  </div>
                </div>
              )}
            </Modal>
          </DashboardContainer>
        </DashboardLayout>
      );
    };
    
    export default TeacherDashboard;

